import { PRESET_IDS, presetRange, type PresetId } from "./date-presets.ts"

/**
 * 반품 목록의 조건은 URL 에 둔다 — 뒤로가기·링크 공유가 그냥 되고, 대시보드 카드가
 * 그대로 링크가 된다. 그래서 "URL 을 어떻게 고칠 것인가" 가 이 화면의 판단 대부분이다.
 * JSX 안 조건식으로 두면 테스트할 수 없어 여기 순수 함수로 모은다.
 */

/** 기간 칩. 프리셋들과 "직접입력". `null` 은 전체. */
export type PeriodId = PresetId | "custom"

/** 조건이 바뀌면 지워야 하는 값들. `null` 은 그 열쇠를 URL 에서 뺀다는 뜻이다. */
export type FilterChanges = Record<string, string | null>

/**
 * 목록을 다시 읽을 조건만 남긴 질의 문자열.
 *
 * `detail`·`tab` 은 옆판을 여닫을 뿐이라 목록을 다시 읽을 이유가 없다 — 빼지 않으면
 * 건 하나를 열 때마다 목록 전체를 다시 받는다.
 */
export function listQuery(params: URLSearchParams): string {
  const next = new URLSearchParams(params)
  next.delete("detail")
  next.delete("tab")
  return next.toString()
}

/**
 * 조건을 바꾼 새 URL 질의.
 *
 * 빈 값은 열쇠째 뺀다(`?status=` 같은 흔적을 남기지 않는다). 조건이 바뀌면 첫 페이지부터
 * 본다 — 3쪽을 보다 조건을 좁히면 있지도 않은 3쪽이 열린다.
 */
export function withFilterChanges(
  params: URLSearchParams,
  changes: FilterChanges,
): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const [key, value] of Object.entries(changes)) {
    if (value === null || value === "") next.delete(key)
    else next.set(key, value)
  }
  if (!("page" in changes)) next.delete("page")
  return next
}

/** 기간 칩의 문구 열쇠. 칩 자신과 "지금 걸린 조건" 칩이 같은 말을 쓰게 한다. */
export function periodLabelKey(id: PeriodId): string {
  if (id === "custom") return "cases.periodCustom"
  return `cases.period${id === "today" ? "Today" : id}`
}

export const PERIOD_IDS: PeriodId[] = [...PRESET_IDS, "custom"]

/**
 * 기간 칩을 눌렀을 때 URL 을 어떻게 고칠까.
 *
 * 누르면 그 기간으로, 눌린 칩을 다시 누르면 전체로 돌아간다. "직접입력"은 달력 칸을 여는
 * 자리라서, 이미 열려 있으면 **아무 일도 하지 않는다**(`null`) — 적어 둔 날짜가 지워지면 안 된다.
 */
export function periodChange(
  next: PeriodId | null,
  current: PeriodId | null,
  today: Date,
): FilterChanges | null {
  if (next === null) return { from: null, to: null }
  if (next === "custom") {
    if (current === "custom") return null
    // 달력을 열 때는 최근 30일을 채워 둔다 — 빈 칸 두 개보다 고치기 쉽다.
    return presetRange("30d", today)
  }
  return presetRange(next, today)
}

export type FilterChipSpec = {
  key: string
  /** 사전 열쇠. 없으면 `labelText` 를 그대로 보여 준다. */
  labelKey?: string
  labelText?: string
  /** 이 칩의 x 를 눌렀을 때 지울 것. */
  clear: FilterChanges
}

/** 조건 하나도 남기지 않는다("전체 해제"). */
export const CLEAR_ALL_FILTERS: FilterChanges = {
  q: null,
  status: null,
  from: null,
  to: null,
  card: null,
}

/**
 * 지금 걸려 있는 조건을 지울 수 있는 칩으로. 필터 칸을 접어 두고도
 * "무엇 때문에 이만큼만 보이는지" 가 보이게 한다.
 */
export function activeFilterChips(filters: {
  card: string | null
  q: string | null
  status: string | null
  from: string | null
  to: string | null
  /** 지금 기간이 어느 프리셋인지(아니면 null — 직접 입력한 범위다). */
  preset: PresetId | null
}): FilterChipSpec[] {
  const chips: FilterChipSpec[] = []

  if (filters.card) {
    chips.push({ key: "card", labelKey: `card.${filters.card}`, clear: { card: null } })
  }
  if (filters.q) {
    chips.push({ key: "q", labelText: `"${filters.q}"`, clear: { q: null } })
  }
  if (filters.status) {
    chips.push({
      key: "status",
      labelKey: `status.${filters.status}`,
      clear: { status: null },
    })
  }
  if (filters.from || filters.to) {
    chips.push({
      key: "period",
      ...(filters.preset
        ? { labelKey: periodLabelKey(filters.preset) }
        : { labelText: `${filters.from ?? "…"} ~ ${filters.to ?? "…"}` }),
      clear: { from: null, to: null },
    })
  }

  return chips
}
